import { z } from 'zod';
import type { FoodRow } from '@/lib/db';
import { keyWords, normalize, searchFoods, type SearchDoc } from '@/lib/search';
import type MiniSearch from 'minisearch';

/**
 * Traduce lo que devuelve el LLM a alimentos del catálogo local.
 *
 * El LLM SOLO parte el texto en {alimento, cantidad, unidad}. No aporta ni un
 * solo valor nutricional: los números salen siempre de la base de datos local
 * (SPEC 8). Si inventara un alimento que no existe, aquí se queda en "no
 * encontrado" y el usuario lo resuelve a mano.
 */
export const mealItemSchema = z.object({
  alimento: z.string().trim().min(1).max(80),
  cantidad: z.number().positive().max(10_000),
  unidad: z.string().trim().min(1).max(20),
});

export const mealItemsSchema = z.array(mealItemSchema).min(1).max(15);

export type MealItem = z.infer<typeof mealItemSchema>;

/** Unidades que sabemos convertir. Lo demás se trata como unidades domésticas. */
const GRAMS_PER_UNIT: Record<string, number> = {
  g: 1,
  gr: 1,
  gramo: 1,
  gramos: 1,
  kg: 1000,
  kilo: 1000,
  kilos: 1000,
  // El agua pesa lo que mide; para el resto es una aproximación aceptable en
  // líquidos de cocina, y el usuario puede corregir los gramos antes de añadir.
  ml: 1,
  mililitro: 1,
  mililitros: 1,
  l: 1000,
  litro: 1000,
  litros: 1000,
};

export interface ResolvedItem {
  /** Lo que dijo el LLM, tal cual, para poder enseñarlo si falla la búsqueda. */
  raw: MealItem;
  food: FoodRow | null;
  grams: number | null;
  /** Otros candidatos que también casan, para cambiar en la revisión (el primero es `food`). */
  alternatives: FoodRow[];
  /** Por qué no se ha podido resolver, si es el caso. */
  problem?: 'no_encontrado' | 'sin_cantidad';
}

/** Convierte cantidad + unidad a gramos usando las porciones del alimento. */
export function toGrams(item: MealItem, food: FoodRow | null): number | null {
  const unit = normalize(item.unidad);

  const direct = GRAMS_PER_UNIT[unit];
  if (direct != null) return round1(item.cantidad * direct);

  if (!food) return null;

  // "1 taza", "2 rebanadas": se busca entre las porciones domésticas del propio
  // alimento, que vienen de USDA y son medidas reales, no inventadas.
  const portion = food.householdPortions.find((p) => {
    const label = normalize(p.label);
    return label === unit || unit.startsWith(label) || label.startsWith(unit);
  });
  if (portion) return round1(item.cantidad * portion.grams);

  // "1 unidad" / "1 pieza": una porción que sea UNA PIEZA (mediana antes que
  // grande o pequeña), nunca una taza ni una cucharada. Antes se cogía la
  // primera, fuera la que fuera, y "1 naranja" era 1 taza de gajos.
  if (/^(unidad|unidades|pieza|piezas|ud|uds)$/.test(unit)) {
    for (const piece of PIECE_PORTIONS) {
      const portion = food.householdPortions.find((p) => piece.test(normalize(p.label)));
      if (portion) return round1(item.cantidad * portion.grams);
    }
  }

  return null;
}

/** Porciones que son una pieza, de la más representativa a la menos. */
const PIECE_PORTIONS = [
  /median[oa]/,
  /^unidad$/,
  /^pieza/,
  /^envase/,
  /^unidad/,
  /grande/,
  /pequen[oa]/,
];

/**
 * Busca cada alimento en el catálogo local.
 *
 * Nunca se queda con una coincidencia mala en silencio: si no encuentra nada,
 * lo marca para que la interfaz lo pregunte.
 */
export function resolveMealItems(
  items: readonly MealItem[],
  index: MiniSearch<SearchDoc>,
  byId: ReadonlyMap<string, FoodRow>,
): ResolvedItem[] {
  return items.map((raw) => {
    // La búsqueda de la app es deliberadamente indulgente (acentos, erratas, OR
    // de rescate) porque ahí el usuario VE la lista y elige. Aquí no la ve, así
    // que solo valen los candidatos que contienen TODAS las palabras pedidas.
    const alternatives = searchFoods(index, raw.alimento, { limit: MAX_CANDIDATES })
      .map((id) => byId.get(id))
      .filter((f): f is FoodRow => f != null && coversAllWords(raw.alimento, f))
      .slice(0, MAX_ALTERNATIVES);
    const food = alternatives[0] ?? null;

    if (!food) return { raw, food: null, grams: null, alternatives, problem: 'no_encontrado' };

    const grams = toGrams(raw, food);
    if (grams == null || grams <= 0) {
      return { raw, food, grams: null, alternatives, problem: 'sin_cantidad' };
    }

    return { raw, food, grams, alternatives };
  });
}

/**
 * ¿El alimento contiene TODAS las palabras con significado de lo pedido?
 *
 * Acepta "arroz blanco" frente a "Arroz, blanco, cocido" y rechaza lo que solo
 * comparte una palabra: con que coincidiera una, "café con leche" daba leche
 * condensada y "tortilla de patatas", patatas fritas de bolsa.
 */
function coversAllWords(query: string, food: FoodRow): boolean {
  const asked = keyWords(query);
  if (asked.length === 0) return false;

  const known = new Set([...keyWords(food.name), ...food.synonyms.flatMap(keyWords)]);
  return asked.every((word) => known.has(word));
}

/** Cuántos resultados se miran y cuántos se ofrecen para cambiar en la revisión. */
const MAX_CANDIDATES = 12;
const MAX_ALTERNATIVES = 5;

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}
